// src/components/admin/PushTokenAdmin.js
// Updated: 02/13/2026 - Added Collapsible Church Groups & User Management Styling

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, 
  Button, CircularProgress, Alert, Chip, Tooltip, TextField, MenuItem, 
  Select, FormControl, InputLabel, TableSortLabel, FormControlLabel, Switch, Grid,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { Sync, PhoneIphone, Web, Search, ExpandMore } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const PushTokenAdmin = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tokenData, setTokenData] = useState([]);
  const [churchList, setChurchList] = useState([]);

  // Controls State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [churchFilter, setChurchFilter] = useState('all');
  const [groupByChurch, setGroupByChurch] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'lastLoginAtRaw', direction: 'desc' });

  // Format timestamps safely
  const formatDate = (timestamp) => {
    if (!timestamp) return "Never";
    if (timestamp.seconds) return new Date(timestamp.seconds * 1000).toLocaleString();
    return new Date(timestamp).toLocaleString();
  };

  const getRawTime = (timestamp) => {
    if (!timestamp) return 0;
    if (timestamp.seconds) return timestamp.seconds;
    return new Date(timestamp).getTime() / 1000;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let usersQuery;
      let churchMap = {};

      if (userProfile.role === 'Super Administrator') {
        const churchesSnap = await getDocs(collection(db, 'churches'));
        const cList = [];
        churchesSnap.forEach(cDoc => {
            churchMap[cDoc.id] = cDoc.data().name || 'Unknown Church';
            cList.push({ id: cDoc.id, name: cDoc.data().name || 'Unknown Church' });
        });
        setChurchList(cList.sort((a, b) => a.name.localeCompare(b.name)));
        usersQuery = query(collection(db, 'users'));
      } else if (userProfile.role === 'Church Administrator' && userProfile.churchId) {
        usersQuery = query(collection(db, 'users'), where('churchId', '==', userProfile.churchId));
      } else {
        setLoading(false);
        return; 
      }

      const usersSnap = await getDocs(usersQuery);
      let allDeviceTokens = [];

      for (const userDoc of usersSnap.docs) {
        const userData = userDoc.data();
        const tokensSnap = await getDocs(collection(db, `users/${userDoc.id}/fcmTokens`));
        const resolvedChurchName = churchMap[userData.churchId] || (userData.churchId ? 'Unknown Church' : 'Unassigned / System');
        
        const baseUser = {
            userId: userDoc.id,
            name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
            role: userData.role || 'Member',
            lastLoginAt: userData.lastLoginAt,
            lastLoginAtRaw: getRawTime(userData.lastLoginAt),
            churchId: userData.churchId || 'unassigned',
            churchName: resolvedChurchName,
        };

        if (tokensSnap.empty) {
            allDeviceTokens.push({
                ...baseUser,
                id: `no-token-${userDoc.id}`,
                token: null,
                device: "None",
                updatedAt: null,
                updatedAtRaw: 0,
                isPwa: false
            });
        } else {
            tokensSnap.forEach(tokenDoc => {
                const tData = tokenDoc.data();
                allDeviceTokens.push({
                    ...baseUser,
                    id: tokenDoc.id,
                    token: tData.token,
                    device: tData.ua ? tData.ua.split(' ')[0] : 'Unknown',
                    updatedAt: tData.updatedAt,
                    updatedAtRaw: getRawTime(tData.updatedAt),
                    isPwa: tData.isPwa || false
                });
            });
        }
      }

      setTokenData(allDeviceTokens);
    } catch (error) {
      console.error("Error fetching tokens:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (userProfile) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  const handleForceSync = async (userId, userName) => {
    if (!window.confirm(`Force a remote token sync for ${userName}?`)) return;
    try {
      await updateDoc(doc(db, 'users', userId), { forceTokenSyncAt: serverTimestamp() });
      alert(`Sync signal sent to ${userName}. If their app is open, it will refresh silently.`);
    } catch (error) {
      console.error("Error forcing sync:", error);
      alert("Failed to send sync signal.");
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Process Data: Filter, Search, and Sort
  const processedData = useMemo(() => {
    let result = [...tokenData];

    if (searchQuery) {
        const q = searchQuery.toLowerCase();
        result = result.filter(r => 
            r.name.toLowerCase().includes(q) || 
            r.role.toLowerCase().includes(q) ||
            r.churchName.toLowerCase().includes(q)
        );
    }

    if (statusFilter === 'active') result = result.filter(r => r.token !== null);
    if (statusFilter === 'missing') result = result.filter(r => r.token === null);

    if (churchFilter !== 'all') {
        result = result.filter(r => r.churchId === churchFilter);
    }

    result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return result;
  }, [tokenData, searchQuery, statusFilter, churchFilter, sortConfig]);

  // Grouping Logic
  const groupedData = useMemo(() => {
      const groups = {};
      processedData.forEach(row => {
          const cName = row.churchName;
          if (!groups[cName]) groups[cName] = [];
          groups[cName].push(row);
      });
      return groups;
  }, [processedData]);

  const isSuperAdmin = userProfile?.role === 'Super Administrator';

  if (!isSuperAdmin && userProfile?.role !== 'Church Administrator') {
    return <Alert severity="error">You do not have permission to view this page.</Alert>;
  }

  // --- REUSABLE TABLE COMPONENT ---
  const renderTable = (dataSubset, showChurchColumn) => (
    <Box sx={{ overflowX: 'auto', width: '100%' }}>
      <Table size="small">
        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
          <TableRow>
            <TableCell>
                <TableSortLabel active={sortConfig.key === 'name'} direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'} onClick={() => handleSort('name')}>
                  <strong>User</strong>
                </TableSortLabel>
            </TableCell>
            {showChurchColumn && (
              <TableCell>
                <TableSortLabel active={sortConfig.key === 'churchName'} direction={sortConfig.key === 'churchName' ? sortConfig.direction : 'asc'} onClick={() => handleSort('churchName')}>
                  <strong>Church</strong>
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell>
                <TableSortLabel active={sortConfig.key === 'lastLoginAtRaw'} direction={sortConfig.key === 'lastLoginAtRaw' ? sortConfig.direction : 'desc'} onClick={() => handleSort('lastLoginAtRaw')}>
                  <strong>Last Login</strong>
                </TableSortLabel>
            </TableCell>
            <TableCell><strong>Device Type</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell>
                <TableSortLabel active={sortConfig.key === 'updatedAtRaw'} direction={sortConfig.key === 'updatedAtRaw' ? sortConfig.direction : 'desc'} onClick={() => handleSort('updatedAtRaw')}>
                  <strong>Last Checked-in</strong>
                </TableSortLabel>
            </TableCell>
            <TableCell align="right"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dataSubset.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">{row.name}</Typography>
                <Typography variant="caption" color="textSecondary">{row.role}</Typography>
              </TableCell>
              {showChurchColumn && (
                  <TableCell><Typography variant="body2">{row.churchName}</Typography></TableCell>
              )}
              <TableCell><Typography variant="body2">{formatDate(row.lastLoginAt)}</Typography></TableCell>
              <TableCell>
                  {row.token ? (
                      <Chip icon={row.isPwa ? <PhoneIphone fontSize="small"/> : <Web fontSize="small"/>} label={row.isPwa ? "PWA App" : "Browser"} size="small" color={row.isPwa ? "primary" : "default"} variant="outlined" />
                  ) : "N/A"}
              </TableCell>
              <TableCell>
                {row.token ? (
                  <Tooltip title={row.token} placement="top"><Chip label="Active" size="small" color="success" /></Tooltip>
                ) : (
                  <Chip label="Missing" size="small" color="error" />
                )}
              </TableCell>
              <TableCell><Typography variant="body2">{formatDate(row.updatedAt)}</Typography></TableCell>
              <TableCell align="right">
                  {isSuperAdmin && (
                      <Button size="small" variant="outlined" startIcon={<Sync />} onClick={() => handleForceSync(row.userId, row.name)}>
                          Force Sync
                      </Button>
                  )}
              </TableCell>
            </TableRow>
          ))}
          {dataSubset.length === 0 && (
            <TableRow><TableCell colSpan={showChurchColumn ? 7 : 6} align="center" sx={{ py: 3 }}>No records found.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, mb: 8, px: 2 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="primary.main">
        Device & Push Token Management
      </Typography>

      {/* Control Panel */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search name, role, or church..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{ startAdornment: <Search color="action" sx={{mr: 1}}/> }}
                />
            </Grid>
            <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                    <InputLabel>Token Status</InputLabel>
                    <Select value={statusFilter} label="Token Status" onChange={(e) => setStatusFilter(e.target.value)}>
                        <MenuItem value="all">All Statuses</MenuItem>
                        <MenuItem value="active">Active Only</MenuItem>
                        <MenuItem value="missing">Missing Only</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            {isSuperAdmin && (
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Filter by Church</InputLabel>
                        <Select value={churchFilter} label="Filter by Church" onChange={(e) => setChurchFilter(e.target.value)}>
                            <MenuItem value="all">All Churches</MenuItem>
                            <MenuItem value="unassigned">Unassigned / System</MenuItem>
                            {churchList.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
            )}
            {isSuperAdmin && (
                <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <FormControlLabel 
                        control={<Switch checked={groupByChurch} onChange={(e) => setGroupByChurch(e.target.checked)} color="primary" />} 
                        label={<Typography fontWeight="medium">Group by Church</Typography>} 
                    />
                </Grid>
            )}
        </Grid>
      </Paper>
      
      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Flat List View */}
          {!groupByChurch && (
            <Paper elevation={2} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
              {renderTable(processedData, isSuperAdmin)}
            </Paper>
          )}

          {/* Collapsible Grouped View */}
          {groupByChurch && isSuperAdmin && (
            <Box>
              {Object.keys(groupedData).sort().map((churchName) => (
                <Accordion key={churchName} sx={{ mb: 1, borderRadius: 1, '&:before': { display: 'none' }, boxShadow: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: '#f8f9fa', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                        {churchName}
                      </Typography>
                      <Chip 
                        label={`${groupedData[churchName].length} Devices`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ ml: 2, fontWeight: 'bold' }} 
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {renderTable(groupedData[churchName], false)}
                  </AccordionDetails>
                </Accordion>
              ))}
              {Object.keys(groupedData).length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="textSecondary">No users match the current filters.</Typography>
                </Paper>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PushTokenAdmin;